import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Form,
  RadioButtonGroup,
  RadioButton,
  TextInput,
  NumberInput,
  Button,
  MultiSelect,
  Tag,
} from '@carbon/react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { OpenmrsDatePicker, usePatient, useSession, closeWorkspace, showSnackbar } from '@openmrs/esm-framework';
import styles from './ict-service-form.scss';
import type { IctOfferingFormData, ICTFormProps } from '../types';
import dayjs from 'dayjs';
import {
  ictOfferingdefaultValues,
  ictOfferingconceptsUuid,
  PriorityCriteriaOpt,
  NotAccepted,
  yesNoOptions,
} from './utils/form-constant';
import { useFetchLocation } from './hooks/useFetchLocation';
import { defaultValuesFromEncounter } from './hooks/defaultValuesFromEncounter';
import { ICT_GENERAL_ENCOUNTER_TYPE, ICT_OFFERING_ENCOUNTER_TYPE, ictOfferingFormUuid } from '../constants';
import { useTranslation } from 'react-i18next';
import { Accordion } from '@carbon/react';
import { AccordionItem, InlineLoading } from '@carbon/react';
import { createEncounter } from './hooks/createEncounter';
import { useLatestObs } from '../utils/data-table.resource';
import { getLatestObsDate, validatencounter } from '../api/api';
import { ButtonSet } from '@carbon/react';
import { formatDateVal, valueLabelMap } from '../utils/utils';

const IndexCaseOffering: React.FC<ICTFormProps> = ({ encounter, type, mutateEncounters }) => {
  const encounterDate = new Date().toISOString();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const today = new Date();

  const patient = usePatient();
  const session = useSession();
  const age = dayjs().diff(dayjs(patient?.patient?.birthDate), 'year');
  const sex = patient?.patient?.gender;
  const { t } = useTranslation();
  const { facilityInfo } = useFetchLocation();
  const { latestMatched } = useLatestObs(
    patient.patientUuid,
    '160753AAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
    ICT_GENERAL_ENCOUNTER_TYPE,
  );
  const [linkDate, setLinkDate] = useState<Date | null>(null);
  const date_confirmed = latestMatched?.valueDateTime ?? null;
  useEffect(() => {
    getLatestObsDate(patient.patientUuid, 'e2e44119-7633-4d39-97a4-0ceffbb98d91', ICT_GENERAL_ENCOUNTER_TYPE).then(
      setLinkDate,
    );
  }, [patient.patientUuid]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    resetField,
    formState: { errors, touchedFields },
  } = useForm<IctOfferingFormData>({
    defaultValues: encounter
      ? defaultValuesFromEncounter<IctOfferingFormData>(encounter, ictOfferingdefaultValues, ictOfferingconceptsUuid, [
          '8534a956-49e8-42d9-82c4-33e51429265d',
          '59fa6f41-a1d0-45bc-af79-643b2e7dcd23',
        ])
      : ictOfferingdefaultValues,
    mode: 'all',
  });
  const ictOfferingFormValues = useWatch({ control });
  const formData = useWatch({ control });

  const watchAccepted = watch('accepted');
  const watchElicited = watch('elicited');
  const offeredDate = watch('offeredDate');
  const accepteddDate = watch('acceptedDate');

  useEffect(() => {
    // If user changes to "Not Eligible", clear test date
    if (watchElicited !== '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA') {
      setValue('numberOfContacts', null, {
        shouldTouch: true,
      });
      setValue('numberOfContactsLess15Male', null, {
        shouldTouch: true,
      });
      setValue('numberOfContactsLess15Female', null, {
        shouldTouch: true,
      });
      setValue('numberOfContactsAbove15Male', null, {
        shouldTouch: true,
      });
      setValue('numberOfContactsAbove15Female', null, {
        shouldTouch: true,
      });
    }
    if (watchAccepted !== '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA') {
      setValue('elicited', null, {
        shouldTouch: true,
      });
      setValue('acceptedDate', null, {
        shouldTouch: true,
      });
    }
    if (watchAccepted !== '1066AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA') {
      setValue('notAccepted', null, {
        shouldTouch: true,
      });
      setValue('otherReason', null, {
        shouldTouch: true,
      });
    }
  }, [watchElicited, watchAccepted, setValue]);

  const visibilityRules = useMemo(
    () => ({
      notAccepted: (data: IctOfferingFormData) => data.accepted === '1066AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
      //otherReason: (data: IctOfferingFormData) => data.notAccepted === '1066AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
      acceptedDate: (data: IctOfferingFormData) => data.accepted === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
      elicited: (data: IctOfferingFormData) => data.accepted === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
      numberOfContacts: (data: IctOfferingFormData) => data.elicited === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
    }),
    [],
  );

  const encounterFollowUpDate = encounter?.obs?.find(
    (obs) => obs?.concept?.uuid === ictOfferingconceptsUuid.offeredDate,
  )?.value;

  const handleValidate = useCallback(async () => {
    const result = await validatencounter(
      patient.patientUuid,
      ICT_OFFERING_ENCOUNTER_TYPE,
      ictOfferingconceptsUuid.offeredDate,
      formatDateVal(formData.offeredDate),
      new AbortController(),
    );
    if (result?.status) {
      setError('offeredDate', {
        type: 'manual',
      });
      showSnackbar({
        isLowContrast: false,
        kind: 'error',
        title: t('error', 'Error saving encounter'),
        subtitle: t('offeredDate', 'The patient already has an offered record on the selected date.'),
      });
    } else {
      clearErrors('offeredDate');
    }
    return result.status;
  }, [clearErrors, formData.offeredDate, patient.patientUuid, setError, t]);

  const onSubmit = async (data: IctOfferingFormData) => {
    setIsSubmitting(true); // Set submitting state to true
    try {
      if (
        formData.offeredDate &&
        patient.patientUuid &&
        formatDateVal(formData.offeredDate) !== encounterFollowUpDate
      ) {
        const dateDuplicate = await handleValidate();
        if (dateDuplicate) return;
      }
      const encounterOfferedDate = formatDateVal(formData.offeredDate);
      await createEncounter<IctOfferingFormData>(data, {
        namespace: 'rfe-forms',
        touchedFields,
        encounter,
        conceptsUuid: ictOfferingconceptsUuid,
        encounterType: ICT_OFFERING_ENCOUNTER_TYPE,
        formUuid: ictOfferingFormUuid,
        encounterDate: encounterOfferedDate,
        patientUuid: patient.patientUuid,
        providerUuid: session.currentProvider.uuid,
        locationUuid: facilityInfo.uuid,
        visibilityRules,
        encounterUuid: encounter?.uuid,
        mutateEncounters,
        workspaceName: 'ict-service-offering-workspace',
        editMode: type?.edit,
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false); // Reset submitting state
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className={styles.formNEw}>
      <h2 className={styles.heading}>ICT SERVICE OFFERING</h2>
      <div className={styles.scrollableContent}>
        <Stack gap={1} className={styles.container}>
          <Accordion>
            <AccordionItem open title="ICT OFFER INFORMATION" className={styles.formContainer}>
              <div className={styles.fieldWrapper}>
                <Controller
                  name="offeredDate"
                  control={control}
                  rules={{
                    validate: (value) => {
                      let error = null;
                      if (!value) {
                        error = 'Date ICT service offered is required';
                      }

                      return error || true;
                    },
                  }}
                  render={({ field }) => (
                    <OpenmrsDatePicker
                      id="offeredDate"
                      labelText={
                        <span className={styles.label}>
                          {t('offeredDate', 'Date ICT service offered: ')}
                          <div className={styles.required}>*</div>
                        </span>
                      }
                      value={field.value}
                      minDate={linkDate}
                      maxDate={accepteddDate || today}
                      onChange={(date) => {
                        field.onChange(date);
                        field.onBlur();
                      }}
                      onBlur={() => field.onBlur()}
                      invalid={!!errors.offeredDate}
                      invalidText={errors.offeredDate?.message}
                      isReadOnly={type?.view}
                    />
                  )}
                />
              </div>
              <>
                <div className={styles.fieldWrapper}>
                  <Controller
                    name="priorityCriteria"
                    control={control}
                    render={({ field }) => {
                      const initialSelectedItems = PriorityCriteriaOpt.filter((item) =>
                        field?.value?.selectedItems?.some((selected) => selected.concept === item.concept),
                      );
                      return (
                        <MultiSelect
                          readOnly={type?.view}
                          id="priorityCriteria"
                          titleText={
                            <span className={styles.label}>{t('priorityCriteria', 'Prioritization for ICT')}</span>
                          }
                          initialSelectedItems={initialSelectedItems}
                          items={PriorityCriteriaOpt}
                          sortItems={(items) => items}
                          invalid={!!errors.priorityCriteria}
                          invalidText={errors.priorityCriteria?.message}
                          onChange={({ selectedItems }) => {
                            field.onChange({ selectedItems });
                            field.onBlur();
                          }}
                          onBlur={() => field.onBlur()}
                          value={field.value?.selectedItems || []}
                        />
                      );
                    }}
                  />
                  {ictOfferingFormValues?.priorityCriteria?.selectedItems?.map((data) => (
                    <Tag key={data.concept} type="gray">
                      {valueLabelMap[data.label] || data.label}
                    </Tag>
                  ))}
                </div>
                {ictOfferingFormValues?.priorityCriteria?.selectedItems?.some(
                  (item) => item.concept === '5622AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
                ) && (
                  <div className={styles.fieldWrapper}>
                    <Controller
                      name="otherPrioritization"
                      control={control}
                      rules={{
                        maxLength: {
                          value: 500,
                          message: 'Please use a maximum of 500 characters',
                        },
                      }}
                      render={({ field }) => (
                        <TextInput
                          id="otherPrioritization"
                          labelText={<span className={styles.label}>Specify other</span>}
                          {...field}
                          readOnly={type?.view}
                          invalid={!!errors.otherPrioritization}
                          invalidText={errors.otherPrioritization?.message}
                        />
                      )}
                    />
                  </div>
                )}
              </>
              <div className={styles.fieldWrapper}>
                <Controller
                  name="accepted"
                  control={control}
                  rules={{
                    validate: (value) => {
                      let error = null;
                      if (!value) {
                        error = 'Accepted ICT service is required';
                      }

                      return error || true;
                    },
                  }}
                  render={({ field }) => (
                    <RadioButtonGroup
                      orientation="vertical"
                      id="accepted"
                      legendText={
                        <span className={styles.label}>
                          {t('accepted', 'Accepted ICT service? ')}
                          <div className={styles.required}>*</div>
                        </span>
                      }
                      {...field}
                      onChange={(value: string) => field.onChange(value)}
                    >
                      {yesNoOptions.map((item) => (
                        <RadioButton
                          key={item.concept}
                          value={item.concept}
                          labelText={item.label}
                          checked={field.value === item.concept}
                        />
                      ))}
                    </RadioButtonGroup>
                  )}
                />
              </div>
              {watchAccepted === '1066AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                <div
                  className={`${styles.fieldWrapper} ${
                    // Add lastField CSS only if no concept or 'other' not selected
                    !watch('notAccepted') ||
                    !watch('notAccepted')?.selectedItems?.length ||
                    !watch('notAccepted')?.selectedItems?.some(
                      (item) => item.concept === '5622AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA', // replace with actual 'other' concept UUID
                    )
                      ? styles.lastField
                      : ''
                  }`}
                >
                  <Controller
                    name="notAccepted"
                    control={control}
                    render={({ field }) => {
                      const initialSelectedItems = NotAccepted.filter((item) =>
                        field?.value?.selectedItems?.some((selected) => selected.concept === item.concept),
                      );
                      return (
                        <MultiSelect
                          readOnly={type?.view}
                          id="notAccepted"
                          titleText={
                            <span className={styles.label}>{t('notAccepted', 'Reason ICT service not accepted')}</span>
                          }
                          initialSelectedItems={initialSelectedItems}
                          items={NotAccepted}
                          sortItems={(items) => items}
                          invalid={!!errors.notAccepted}
                          invalidText={errors.notAccepted?.message}
                          onChange={({ selectedItems }) => {
                            field.onChange({ selectedItems });
                            field.onBlur();
                          }}
                          onBlur={() => field.onBlur()}
                          value={field.value?.selectedItems || []}
                        />
                      );
                    }}
                  />
                  {ictOfferingFormValues?.notAccepted?.selectedItems?.map((data) => (
                    <Tag key={data.concept} type="gray">
                      {data.label}
                    </Tag>
                  ))}
                </div>
              )}
              {watchAccepted === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                <div className={styles.fieldWrapper}>
                  <Controller
                    name="acceptedDate"
                    control={control}
                    rules={{
                      validate: (value) => {
                        let error = null;
                        if (!value) {
                          error = 'Accepted Date is required';
                        }

                        return error || true;
                      },
                    }}
                    render={({ field }) => (
                      <OpenmrsDatePicker
                        id="acceptedDate"
                        labelText={
                          <span className={styles.label}>
                            {t('acceptedDate', 'Date ICT service accepted: ')}
                            <div className={styles.required}>*</div>
                          </span>
                        }
                        value={field.value || null}
                        minDate={offeredDate || linkDate}
                        maxDate={today}
                        onChange={(date) => {
                          field.onChange(date);
                          field.onBlur();
                        }}
                        onBlur={() => field.onBlur()}
                        invalid={!!errors.acceptedDate}
                        invalidText={errors.acceptedDate?.message}
                        isReadOnly={type?.view}
                      />
                    )}
                  />
                </div>
              )}
              {watchAccepted === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                <div
                  className={`${styles.fieldWrapper} ${
                    !watch('elicited') || watch('elicited') !== '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA'
                      ? styles.lastField
                      : ''
                  }`}
                >
                  <Controller
                    name="elicited"
                    control={control}
                    render={({ field }) => (
                      <RadioButtonGroup
                        orientation="vertical"
                        id="elicited"
                        legendText={<span className={styles.label}>{t('elicited', 'Elicited Contacts?')}</span>}
                        {...field}
                        onChange={(value: string) => field.onChange(value)}
                      >
                        {yesNoOptions.map((item) => (
                          <RadioButton
                            key={item.concept}
                            value={item.concept}
                            labelText={item.label}
                            checked={field.value === item.concept}
                          />
                        ))}
                      </RadioButtonGroup>
                    )}
                  />
                </div>
              )}
              {watchElicited === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                <div className={styles.fieldWrapper}>
                  <div className={styles.lastField}>
                    <Controller
                      name="numberOfContacts"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (value === null || typeof value === 'undefined') {
                            return 'Number of contacts is required';
                          }
                          const num = Number(value);
                          if (isNaN(num)) {
                            return 'Number of contacts should be a valid number';
                          }
                          if (num <= 0) {
                            return 'Number of contacts should be a valid counting number';
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <NumberInput
                          id="numberOfContacts"
                          allowEmpty={false}
                          label={
                            <span className={styles.label}>
                              Number of contacts elicited:
                              <div className={styles.required}>*</div>
                            </span>
                          }
                          //value={field.value}
                          value={field.value}
                          onChange={(_, { value }) => {
                            field.onChange(value !== '' ? Number(value) : null);
                            field.onBlur();
                          }}
                          onKeyDown={(e) => {
                            if (['e', 'E', '+', '-'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          invalid={!!errors.numberOfContacts}
                          invalidText={errors.numberOfContacts?.message}
                          readOnly={type?.view}
                        />
                      )}
                    />
                  </div>
                </div>
              )}

              {ictOfferingFormValues?.notAccepted?.selectedItems?.some(
                (item) => item.concept === '5622AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
              ) && (
                <div className={`${styles.fieldWrapper} ${styles.lastField}`}>
                  <Controller
                    name="otherReason"
                    control={control}
                    rules={{
                      maxLength: {
                        value: 50,
                        message: 'Please use a maximum of 50 characters',
                      },
                    }}
                    render={({ field }) => (
                      <TextInput
                        id="otherReason"
                        labelText={<span className={styles.label}>Specify other reason</span>}
                        {...field}
                        readOnly={type?.view}
                        invalid={!!errors.otherReason}
                        invalidText={errors.otherReason?.message}
                      />
                    )}
                  />
                </div>
              )}
            </AccordionItem>
          </Accordion>
        </Stack>
      </div>
      <ButtonSet className={styles.buttonSet}>
        <Button
          onClick={() => closeWorkspace('ict-service-offering-workspace')}
          style={{ maxWidth: 'none', width: '50%' }}
          className={styles.button}
          kind="secondary"
        >
          {t('discard', 'Discard')}
        </Button>
        <Button
          style={{ maxWidth: 'none', width: '50%' }}
          className={styles.button}
          kind="primary"
          type="submit"
          disabled={type?.view || isSubmitting}
        >
          {isSubmitting ? <InlineLoading description="submitting..." /> : type?.edit ? 'Update' : 'Save'}
        </Button>
      </ButtonSet>
    </Form>
  );
};

export default IndexCaseOffering;
